import { useMemo, useState } from "react";
import { Nav, Tab } from "react-bootstrap";
import { Icon, LoadingIndicator, RenderWhen } from "../../../components";
import SelectRowsProvider from "../../../enhancers/SelectRowsProvider";
import Typography from "../../../components/typography/Typography";
import { useGetTiCardData } from "../../search/components/ticard/hooks";
import { ReportSummaryHeader } from "../components";
import ActorCampaigns from "../components/ioc/cards/ActorCampaigns";
import ActorInsights from "../components/ioc/cards/ActorInsights";
import ActorIOC from "../components/ioc/cards/ActorIOC";
import ActorSources from "../components/ioc/cards/ActorSources";
import ActorTTPS from "../components/ioc/cards/ActorTTPS";
import ActorVulnerabilities from "../components/ioc/cards/ActorVulnerabilities";
import MalwareFamilyCard from "../components/ioc/cards/MalwareFamilyCard";
import RelatedActors from "../components/ioc/cards/RelatedActors";
import SingleActorSampleAttribution from "../components/ioc/cards/SingleActorSampleAttribution";
import useGetCombinedActorData from "../hooks/useGetCombinedActorData";
import useGetIOCSummary from "../hooks/useGetIOCSummary";

interface ThreatIntelligenceContainerProps {
  ioc: string;
  threatName?: string;
  sourceId?: number;
  showHeader?: boolean;
  isDataLoading?: boolean;
}

const ThreatIntelligenceContainer = ({
  ioc,
  threatName,
  sourceId,
  showHeader = true,
  isDataLoading = false
}: ThreatIntelligenceContainerProps) => {
  const { data: malwareFamilyData } = useGetTiCardData(threatName);
  const { data: iocData, isLoading: isIocLoading } = useGetIOCSummary(
    ioc || ""
  );

  const actors = useMemo(() => {
    const iocActors = iocData?.related_actors ?? [];
    const malwareFamilyActors = malwareFamilyData?.rl?.general?.actor ?? [];

    return [
      ...new Set([
        ...iocActors.map(actor => actor.name),
        ...malwareFamilyActors.map(actor => actor.name)
      ])
    ];
  }, [iocData, malwareFamilyData]);

  const {
    combinedIOCs,
    combinedSources,
    isLoading: isCombinedActorDataLoading
  } = useGetCombinedActorData(actors);
  const [activeActorIndex, setActiveActorIndex] = useState(0);

  const getActorName = (index: number) => actors[index];

  const getActorDescription = (index: number) => {
    const actorName = getActorName(index);
    return malwareFamilyData?.rl?.general?.actor?.find(
      actor => actor.name === actorName
    )?.description;
  };

  const hasActors = actors.length > 0;

  return (
    <div className="flex flex-column flex-gap-5">
      {showHeader && <ReportSummaryHeader />}
      <RenderWhen
        condition={!isDataLoading && !isIocLoading}
        fallback={<LoadingIndicator />}
      >
        <div className="flex flex-gap-2 flex-center">
          <Icon name="cloud-intelligence" size={24} />
          <Typography variant="h2">Attribution Data</Typography>
        </div>
        <div>
          <Tab.Container defaultActiveKey="attribution">
            <Nav variant="tabs" className="nav-tabs--bordered">
              <Nav.Item>
                <Nav.Link eventKey="attribution">Attribution</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="malware-family">
                  Malware Family Insights
                </Nav.Link>
              </Nav.Item>
            </Nav>
            <Tab.Content>
              <Tab.Pane eventKey="attribution">
                <RenderWhen
                  condition={hasActors}
                  fallback={
                    <Typography variant="subtitle4">
                      No actor data available for this sample.
                    </Typography>
                  }
                >
                  {actors.length === 1 ? (
                    <div className="flex flex-column flex-gap-3">
                      <SingleActorSampleAttribution sampleIOCData={iocData} />
                      <ActorInsights
                        actorName={getActorName(0)}
                        description={getActorDescription(0)}
                      />
                      <SelectRowsProvider>
                        <ActorIOC actorName={getActorName(0)} />
                      </SelectRowsProvider>
                      <ActorCampaigns actorName={getActorName(0)} />
                      <ActorTTPS actorName={getActorName(0)} />
                      <ActorVulnerabilities actorName={getActorName(0)} />
                      <RelatedActors actorName={getActorName(0)} />
                      <ActorSources actorName={getActorName(0)} />
                    </div>
                  ) : (
                    <Tab.Container
                      id="actor-tabs"
                      defaultActiveKey="0"
                      activeKey={activeActorIndex.toString()}
                      onSelect={key => setActiveActorIndex(Number(key))}
                    >
                      <Nav className="mb-20 border-bottom">
                        {actors.map((actorName, index) => (
                          <Nav.Item key={index}>
                            <Nav.Link
                              eventKey={index.toString()}
                              style={{
                                textDecoration: "underline",
                                fontWeight:
                                  activeActorIndex === index
                                    ? "bold"
                                    : "normal",
                                cursor: "pointer"
                              }}
                            >
                              {actorName.charAt(0).toUpperCase() +
                                actorName.slice(1)}
                            </Nav.Link>
                          </Nav.Item>
                        ))}
                      </Nav>
                      <Tab.Content>
                        {actors.map((_, index) => (
                          <Tab.Pane key={index} eventKey={index.toString()}>
                            <div className="flex flex-column flex-gap-3">
                              <SingleActorSampleAttribution
                                sampleIOCData={iocData}
                              />
                              <ActorInsights
                                actorName={getActorName(index)}
                                description={getActorDescription(index)}
                              />
                              <SelectRowsProvider>
                                <ActorIOC actorName={getActorName(index)} />
                              </SelectRowsProvider>
                              <ActorCampaigns actorName={getActorName(index)} />
                              <ActorTTPS actorName={getActorName(index)} />
                              <ActorVulnerabilities
                                actorName={getActorName(index)}
                              />
                              <RelatedActors actorName={getActorName(index)} />
                              <ActorSources actorName={getActorName(index)} />
                            </div>
                          </Tab.Pane>
                        ))}
                      </Tab.Content>
                    </Tab.Container>
                  )}
                </RenderWhen>
              </Tab.Pane>
              <Tab.Pane eventKey="malware-family">
                <div className="flex flex-column flex-gap-3">
                  <MalwareFamilyCard
                    threatName={threatName}
                    hash={ioc}
                    sourceId={sourceId}
                    showLink={false}
                    renderThreatAdditionalInfo
                  />
                  <RenderWhen
                    condition={!isCombinedActorDataLoading}
                    fallback={<LoadingIndicator />}
                  >
                    <SelectRowsProvider>
                      <ActorIOC iocs={combinedIOCs} />
                    </SelectRowsProvider>
                    <ActorSources sources={combinedSources} />
                  </RenderWhen>
                </div>
              </Tab.Pane>
            </Tab.Content>
          </Tab.Container>
        </div>
      </RenderWhen>
    </div>
  );
};

export default ThreatIntelligenceContainer;
